/*
 * Types for palchiki-api responses (app "palchiki-web").
 *
 * Shared shapes follow facelogy-site-client/src/api/api.types.ts (same API).
 * Page template vars follow the CMS schemas in
 * palchiki-cloud/modules/custom/cms/tpl/scripts/pages/palchiki-web/*.js
 * (snake_case there, camelCase in API responses).
 */

export enum MenuItemType {
  external = 1,
  page = 2,
  file = 3,
}

export type LinkedPage = {
  id: number,
  name: string,
  fullPath: string,
}

export type LinkedFile = {
  id: number,
  path: string,
  link: string,
  name: string,
}

export type MenuItem = {
  title: string,
  type: MenuItemType,
  linkedPage: LinkedPage | null,
  path: string | null,
  file: LinkedFile | null,
}

export type SiteMenu = {
  top: MenuItem[],
  bottom: MenuItem[],
}

export type Redirect = {
  status: number,
  url: string,
  linkedPage: LinkedPage,
}

export type FooterBlock = {
  title: string,
  content: string,
}

export type City = {
  city: {
    id: number,
    name: string,
    timezone: string,
  },
  coords: string,
  socials: {
    vk: string,
    fb: string,
    tg: string,
    ig: string,
  },
}

export type SiteSettings = {
  domain: string,
  cities: City[],
  menu: SiteMenu,
  scripts?: Array<{ title: string, script: string }>,
  redirects: Redirect[],
  footerBlock1: FooterBlock,
  footerBlock2: FooterBlock,
  footerBlock3: FooterBlock,
}

/* ---------- media ---------- */

export type Image = {
  id: number,
  resolution: string,
  type: string,
  path: string,
  link: string,
  name: string,
}

export type ImageSet = Image[]

type MediaImage = {
  id: number,
  type: 'image',
  image: ImageSet,
  imageAlt?: string,
  title?: string,
  youtubeLink: null,
}

type MediaYoutube = {
  id: number,
  type: 'youtube',
  image: null,
  title?: string,
  youtubeLink: string,
}

export type Media = MediaImage | MediaYoutube

/* ---------- pages ---------- */

export type Page<T> = {
  id: number,
  name: string,
  title: string,
  fullPath: string,
  publishedAt: string,
  noIndex?: boolean,
  templateVars: {
    seo?: {
      keywords: string,
      description: string,
      noIndex: boolean,
    },
    pageVars: T,
  },
}

export enum SlideLinkType {
  page = 1,
  external = 2,
}

export type Slide = {
  cities: number[],
  image: Media,
  title: string,
  link: {
    type: SlideLinkType,
    linkedPage: LinkedPage | null,
    externalLink: string | null,
  },
}

export type MainPage = { template: 'main-page' } & Page<{
  slides: Slide[],
}>

export type TextPage = { template: 'text-page' } & Page<{
  content: string,
}>

export enum NewsActionType {
  appointment = 1,
  link = 2,
}

export type NewsAction = {
  type: NewsActionType,
  externalLink?: string | null,
  appointment?: {
    salon: { id: number } | null,
    service: { id: number } | null,
  },
}

type NewsItemVars = {
  image: Media,
  shortText: string,
  fullText: string,
  actionText?: string,
  action?: NewsAction | null,
  cities: number[],
}

export type NewsListPage = { template: 'news-list' } & Page<undefined>
export type PromotionsListPage = { template: 'promotions-list' } & Page<undefined>
export type NewsItemPage = { template: 'news-page' } & Page<NewsItemVars>
export type PromotionPage = { template: 'promotion-page' } & Page<NewsItemVars>

export type SalonsListPage = { template: 'salons-list' } & Page<undefined>

/** Palchiki service site types (Ls.data.mod_services.mobile_type_palchiki) */
export enum ServiceSiteType {
  manicure = 1,
  pedicure = 2,
  complex = 3,
  complexCare = 4,
  moisturizing = 5,
  strengthening = 6,
  cleansing = 7,
  additional = 8,
  designs = 9,
  brows = 10,
  chevelure = 12,
  podology = 13,
}

/** One option of a service page, e.g. a nail length; its title is shown next to the page title */
export type ServicePageOption = {
  title: string,
  service: Omit<Service, 'meta'>,
}

export type ServicePage = { template: 'service-page' } & Page<{
  services?: ServicePageOption[],
  /** Pages saved before options existed have one service here instead of services */
  service?: Omit<Service, 'meta'>,
  isHidden?: boolean,
  type: ServiceSiteType,
  forMen?: boolean,
  description: string,
  altDescription: string,
  sort?: string,
  images: Media[],
}>

export type SalonOptions = {
  hasParking: boolean,
  hasCoffee: boolean,
  hasWifi: boolean,
  hasRamp: boolean,
  hasKids: boolean,
  hasBrows: boolean,
}

export type SalonPageVars = {
  salon: Omit<Salon, 'meta'>,
  salonName: string,
  salonAddress: string,
  salonCoords: string,
  salonPhones: Array<{ phone: string }>,
  options: SalonOptions,
  description: string,
  images: Media[],
}

export type SalonPage = { template: 'salon-page' } & Page<SalonPageVars>

export type ContactsPage = { template: 'contacts-page' } & Page<{
  blocks: Array<{
    title: string,
    icon: Media | null,
    address: string,
    phone: string,
    email: string,
    schedule: string,
    comments: string,
  }>,
}>

export type GiftCard = {
  id: number,
  name: string,
  sum: number,
  image: string,
}

export type GiftCardsPage = { template: 'gift-card-page' } & Page<{
  content: string,
  cards: Array<{ card: GiftCard }>,
  rulesFile: LinkedFile | null,
  personalFile: LinkedFile | null,
  privacyFile: LinkedFile | null,
  paymentFile: LinkedFile | null,
  conditionsFile: LinkedFile | null,
}>

export type MobilePage = { template: 'mobile-page' } & Page<{
  content: string,
  appstore: string,
  googleplay: string,
  image: Media,
}>

export type AboutPage = { template: 'about-page' } & Page<{
  blocks: Array<{
    title: string,
    content: string,
    image: Media | null,
    imageSmall: Media | null,
    highlighted: boolean,
  }>,
}>

type ClubLevel = {
  title: string,
  description: string,
}

export type ClubPage = { template: 'club-page' } & Page<{
  subtitle: string,
  level1: ClubLevel,
  level2: ClubLevel,
  level3: ClubLevel,
  blueList: {
    title: string,
    text: string,
  },
  info: Array<{
    icon: Media | null,
    description: string,
  }>,
}>

export type PromoPage = { template: 'promo-page' } & Page<{
  content: string,
  /** CMS autocomplete value of the linked salon */
  salon: { id: number | string, phone?: string } | null,
  image: Media | null,
}>

export type RedirectPage = {
  template: 'redirect',
  status: number,
  to: string,
}

export type NotFoundPage = {
  template?: undefined,
  fullPath: string,
}

export type AnyPage =
  MainPage |
  TextPage |
  NewsListPage |
  NewsItemPage |
  PromotionsListPage |
  PromotionPage |
  SalonsListPage |
  SalonPage |
  ServicePage |
  ContactsPage |
  GiftCardsPage |
  MobilePage |
  AboutPage |
  ClubPage |
  PromoPage |
  RedirectPage |
  NotFoundPage

export type AnyTemplatePage = Exclude<AnyPage, RedirectPage | NotFoundPage>

/* ---------- data ---------- */

export type Salon = {
  id: number,
  cityId: number,
  divisions: number[],
  businessHours: string,
  meta: SalonPage | null,
}

export type Service = {
  id: number,
  duration: number,
  type: number,
  isAmountChangeable: boolean,
  division: number,
  pricesBySalon: Record<string, number>,
  pricesByCity: Record<string, number[]>,
  salons: number[],
  meta: ServicePage | null,
}

export type PagesList<P> = {
  pages: P[],
  total: number,
}

export type NewsList = PagesList<NewsItemPage>
export type PromotionsList = PagesList<PromotionPage>

/* ---------- auth & profile ---------- */

export type RegistrationRequest = {
  defaultCityId: number,
  firstName: string,
  lastName: string,
  sex: number | null,
  phone: string,
  email: string,
  password: string,
  birthday: string | null,
  passwordConfirm: string,
  code: string,
}

export type QuickRegistrationRequest = {
  defaultCityId: number,
  firstName: string,
  lastName: string,
  phone: string,
  code: string,
}

export type ConfirmPhonePurpose = 'sign-up' | 'change-password'

export type Profile = {
  areNotificationsEnabled: boolean,
  avatar: string,
  birthday: string,
  defaultCityId: number,
  email: string,
  firstName: string,
  id: number,
  lastName: string,
  name: string,
  phone: string,
  sex: number,
  appointmentsCount?: number,
  giftsCount?: number,
}

export type ReviewRequest = {
  id: number,
  answers: Record<string, number>,
  comment: string,
  photos: string[],
}

export type AbstractReviewRequest = {
  comment: string,
  photos: string[],
}

export type CashbackRuleProgress = {
  success: boolean,
  actualValue: number,
  desiredValue: number,
  daysRestriction: number | null,
}

export type CashbackLevel = {
  cashbackAmount: number,
  goods: CashbackRuleProgress,
  services: CashbackRuleProgress,
  checks: CashbackRuleProgress,
}

export type Cashback = {
  program: {
    id: number,
    name: string,
  },
  progress: {
    currentLevel: number | null,
    totalLevels: number,
    levels: CashbackLevel[],
  },
  amount: number,
  closestWriteOff: {
    expiresDate: string,
    amount: number,
  } | null,
}

export type GiftProgram = {
  program: {
    id: number | null,
    name: string,
    description: string,
    alwaysVisible?: boolean,
    image: Media | null,
  },
  discounts: Array<{
    id: number,
    level: number,
    discount: {
      id: number,
      name: string,
    },
    issuedAt: string,
    expiresAt: string | null,
    usagesLeft: number,
  }>,
}

/* ---------- appointments ---------- */

/** A 15-minute start slot of the salon day; only free ones can be booked */
export type FreeTimeSlot = {
  time: string,
  isFree: boolean,
}

export type AppointmentServiceRequest = {
  id: number,
  amount: number,
}

export type AppointmentRequest = {
  salon: number,
  /** Services per client: index 0 is the client, further indexes are companions */
  services: Record<number, AppointmentServiceRequest[]> | AppointmentServiceRequest[][],
  date: string,
  time: string,
  comment: string,
  notification: number | null,
}

export enum AppointmentStatus {
  created = 1,
  started = 2,
  completed = 3,
}

export type AppointmentResponse = {
  id: number,
  salonId: number,
  cityId: number,
  status: AppointmentStatus,
  startAt: string,
  date: string,
  time: string,
  rating: number | null,
  price: number,
  duration: number,
  discountAmount: number,
  actualPrice: number,
  cashbackChange: number,
  comment: string,
  notification: number | null,
  salon: Salon | null,
  services: Array<{
    id: number,
    masterId: number,
    duration: number,
    amount: number,
    price: number,
    type: number,
    service: Service | null,
  }>,
}

export enum VisitCancelReason {
  error = 1,
  other = 2,
  anotherTime = 4,
  anotherSalon = 6,
}

/* ---------- gift cards ---------- */

export type GiftCardRecipient = {
  email: string,
  name: string,
  phone: string,
}

export type GiftCardOrderItem = {
  certificate?: GiftCard,
  certificateId?: number,
  amount: number,
  recipient: GiftCardRecipient,
}

export type GiftCardOrder = {
  items: GiftCardOrderItem[],
  discount: number,
}
